import { useCallback } from 'react';

import { TClip } from '@/features/clips/types';
import { TSegmentLogo, TSegmentPeople } from '@/features/segments/types';
import { VIDEO_OFFSET_TIME_CONTROL } from '@/hooks/use-presence-preview-box';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

import {
  FrameAction,
  FrameActionType,
} from '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '../context/BroadcastScheduleProgramContext';

type TUseGlobalViewPresencePreviewProps = {
  broadcastClips: TClip[];
};

export function useGlobalViewPresencePreview({
  broadcastClips,
}: TUseGlobalViewPresencePreviewProps) {
  const {
    globalViewSegments,
    activeClip,
    videoControlRef,
    setVisibleBox,
    setActiveClip,
    setLogoData,
    setPersonaData,
    setPendingSeekTime,
  } = useBroadcastScheduleProgram();

  const handlePresenceClick = useCallback(
    (presence: TSegmentLogo | TSegmentPeople, frame?: FrameActionType) => {
      const segment = globalViewSegments.find(s => s.id === presence.segment_id);
      if (!segment) return;

      const isEnd = frame === FrameAction.END;
      const presenceMs = new Date(presence.start_datetime).getTime();

      const coveringClip =
        broadcastClips.find(clip => {
          const clipStart = new Date(clip.start_datetime).getTime();
          const clipEnd = new Date(clip.end_datetime ?? clip.start_datetime).getTime();
          return presenceMs >= clipStart && presenceMs <= clipEnd;
        }) ?? null;

      const isSameClip = coveringClip?.id === activeClip?.id;
      setActiveClip(coveringClip);

      const referenceStartDatetime = coveringClip
        ? coveringClip.start_datetime
        : segment.start_datetime;

      const referenceDate = new Date(referenceStartDatetime);
      const seekSeconds = Math.max(
        0,
        isEnd
          ? convertISOToSeconds(presence.end_datetime, referenceDate) - VIDEO_OFFSET_TIME_CONTROL
          : convertISOToSeconds(presence.start_datetime, referenceDate) + VIDEO_OFFSET_TIME_CONTROL
      );

      if (presence.parameters) {
        setVisibleBox({
          bbox: presence.parameters.bbox ?? [0, 0, 0, 0],
          frame: seekSeconds,
          entity_label: presence.entity_label ?? '',
          entity_score: presence.entity_score ?? 0,
        });
      }

      if (isSameClip) {
        videoControlRef.current?.seekToSecond(seekSeconds);
      } else {
        setPendingSeekTime(seekSeconds);
      }

      if ('logo_id' in presence) {
        setLogoData(presence as TSegmentLogo);
      } else {
        setPersonaData(presence as TSegmentPeople);
      }
    },
    [
      globalViewSegments,
      broadcastClips,
      activeClip,
      videoControlRef,
      setVisibleBox,
      setActiveClip,
      setLogoData,
      setPersonaData,
      setPendingSeekTime,
    ]
  );

  return { handlePresenceClick };
}
